import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, ChevronDown, Search } from 'lucide-react';
import { EmptyState } from './States';
import { TableSkeleton } from './Skeleton';

/**
 * Toolbar that sits above a table: search on the left, filters and actions right.
 */
export const TableToolbar = ({
    search,
    onSearchChange,
    searchPlaceholder = 'Search…',
    filters,
    actions,
    className = '',
}) => (
    <div
        className={`flex flex-wrap items-center gap-2 px-4 py-3 border-b border-[var(--divider-color)] ${className}`}
    >
        {onSearchChange && (
            <div className="relative w-full sm:w-64">
                <Search
                    size={14}
                    aria-hidden="true"
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
                />
                <input
                    type="search"
                    className="input input-search"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    aria-label={searchPlaceholder}
                />
            </div>
        )}
        {filters && <div className="flex flex-wrap items-center gap-2">{filters}</div>}
        {actions && <div className="flex items-center gap-2 sm:ml-auto">{actions}</div>}
    </div>
);

/** Sortable column header button. */
const SortHeader = ({ column, sort, onSort }) => {
    const active = sort?.key === column.key;
    const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ChevronUp : ChevronDown;
    return (
        <button
            type="button"
            onClick={() => onSort(column.key)}
            className="inline-flex items-center gap-1 hover:text-ink-900 transition-colors duration-150"
            aria-label={`Sort by ${column.header}`}
        >
            {column.header}
            <Icon size={12} className={active ? 'text-primary-600' : 'text-ink-400'} aria-hidden="true" />
        </button>
    );
};

/**
 * Enterprise data table.
 *
 * columns: [{ key, header, render?(row), sortable?, align?, width?, className? }]
 */
export const DataTable = ({
    columns,
    rows = [],
    loading = false,
    rowKey = (r, i) => r.id ?? i,
    onRowClick,
    empty,
    sort,
    onSort,
    selectable = false,
    selected = [],
    onSelectedChange,
    className = '',
    dense = false,
}) => {
    const allSelected = selectable && rows.length > 0 && selected.length === rows.length;

    const toggleAll = () =>
        onSelectedChange?.(allSelected ? [] : rows.map((r, i) => rowKey(r, i)));

    const toggleOne = (key) =>
        onSelectedChange?.(
            selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]
        );

    if (loading) return <TableSkeleton cols={columns.length} />;

    if (!rows.length) {
        return empty || <EmptyState title="No records found" description="Adjust your filters or search to see more." />;
    }

    return (
        <div className={`overflow-x-auto ${className}`}>
            <table className={`table table-hover ${dense ? 'table-compact' : ''}`}>
                <thead>
                    <tr>
                        {selectable && (
                            <th scope="col" className="w-10">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={toggleAll}
                                    aria-label="Select all rows"
                                    className="accent-[var(--primary)] w-3.5 h-3.5 align-middle"
                                />
                            </th>
                        )}
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                scope="col"
                                style={col.width ? { width: col.width } : undefined}
                                className={col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}
                            >
                                {col.sortable && onSort ? (
                                    <SortHeader column={col} sort={sort} onSort={onSort} />
                                ) : (
                                    col.header
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        const key = rowKey(row, i);
                        return (
                            <tr
                                key={key}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                className={onRowClick ? 'cursor-pointer' : undefined}
                            >
                                {selectable && (
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selected.includes(key)}
                                            onChange={() => toggleOne(key)}
                                            aria-label="Select row"
                                            className="accent-[var(--primary)] w-3.5 h-3.5 align-middle"
                                        />
                                    </td>
                                )}
                                {columns.map((col) => (
                                    <td
                                        key={col.key}
                                        className={[
                                            col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : '',
                                            col.className || '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                    >
                                        {col.render ? col.render(row, i) : row[col.key] ?? <span className="text-muted">—</span>}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

/** Page navigation + result count + page size. */
export const Pagination = ({
    page,
    totalPages,
    total,
    pageSize,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 25, 50, 100],
    className = '',
}) => {
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);

    const pages = useMemo(() => {
        const out = [];
        const window = 1;
        for (let p = 1; p <= totalPages; p += 1) {
            if (p === 1 || p === totalPages || (p >= page - window && p <= page + window)) out.push(p);
            else if (out[out.length - 1] !== '…') out.push('…');
        }
        return out;
    }, [page, totalPages]);

    return (
        <div
            className={`flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-t border-[var(--divider-color)] ${className}`}
        >
            <p className="text-label text-ink-500 tabular">
                Showing <span className="font-medium text-ink-700">{from}–{to}</span> of{' '}
                <span className="font-medium text-ink-700">{total}</span>
            </p>

            <div className="flex items-center gap-3">
                {onPageSizeChange && (
                    <label className="flex items-center gap-1.5 text-label text-ink-500">
                        Rows
                        <select
                            className="input input-sm w-auto"
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        >
                            {pageSizeOptions.map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                <nav className="flex items-center gap-0.5" aria-label="Pagination">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-icon"
                        disabled={page <= 1}
                        onClick={() => onPageChange(page - 1)}
                        aria-label="Previous page"
                    >
                        <ChevronLeft size={15} aria-hidden="true" />
                    </button>
                    {pages.map((p, i) =>
                        p === '…' ? (
                            <span key={`gap-${i}`} className="px-1.5 text-label text-ink-400">
                                …
                            </span>
                        ) : (
                            <button
                                key={p}
                                type="button"
                                onClick={() => onPageChange(p)}
                                aria-current={p === page ? 'page' : undefined}
                                className={`btn btn-sm min-w-[30px] px-2 ${p === page ? 'btn-primary' : 'btn-ghost'}`}
                            >
                                {p}
                            </button>
                        )
                    )}
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-icon"
                        disabled={page >= totalPages}
                        onClick={() => onPageChange(page + 1)}
                        aria-label="Next page"
                    >
                        <ChevronRight size={15} aria-hidden="true" />
                    </button>
                </nav>
            </div>
        </div>
    );
};

/** Local sort state helper shared by list pages. */
export const useSort = (initial = null) => {
    const [sort, setSort] = useState(initial);
    const onSort = (key) =>
        setSort((s) =>
            s?.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
        );
    return { sort, onSort, setSort };
};

export default DataTable;
